import React, { useCallback } from 'react';

import { Box } from '@rbilabs/component-library';
import { defineMessages, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { ClickableContainer } from 'components/clickable-container';
import { SecondaryLink } from 'components/link';
import Picture from 'components/picture';
import SanityBlockRenderer, { SanityBlockContentLinkProps } from 'components/sanity-block-renderer';
import { useGetAllRollUpTheRimsQuery } from 'generated/sanity-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames } from 'state/mParticle';

import { useLocalizedUrls } from '../hooks/use-localized-urls';
import { GameplayBackgroundGradient } from '../sections/gameplay/components/gameplay-background/gameplay-background';
import { TypographyContainer, TypographyContainerProps } from '../typography/sanity-block-content';

import { HomepageBannerConfetti } from './assets/homepage-banner-confetti';
import { IHomepageBannerProps, IHomepageBannerViewProps } from './types';

const BannerWrapper = styled.div`
  color: ${Styles.color.white};
  position: relative;
  margin: 1.25rem 1.25rem 0 1.25rem;
  overflow: hidden;
  border-radius: 8px;
`;

/* override some styles from SanityBlockContent specific to this banner*/
const HomepageBannerTypography = styled(TypographyContainer)<TypographyContainerProps>`
  h2 {
    letter-spacing: -0.78px;
    font-size: 1.56rem;
    margin: 0.5rem 0;

    em {
      transform: rotate(-5deg) translateY(-0.6em);
      display: block;
      font-weight: normal;
      letter-spacing: -0.78px;
      font-size: 1.56rem;
    }
  }
`;

const ContentWrapper = styled(ClickableContainer)`
  display: flex;
  justify-content: center;
  cursor: pointer;
`;

const ConfettiContainer = styled.div`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: auto;
`;

const Cup = styled(Picture)`
  align-self: center;
  position: relative;
  z-index: ${Styles.zIndex.below};
  max-width: 100px;
  height: 150px;
`;

const RulesText = styled.p`
  font-size: 0.8125rem;
  margin: 0;
  padding: 0 1rem 1rem;
`;

const StyledLink = styled(SecondaryLink)`
  font-size: 0.8125rem;
  font-weight: ${Styles.fontWeight.heavy};
`;

const LinkText = styled.span`
  text-decoration: underline;
`;

const LinkTriangle = styled.span`
  font-size: 0.75em;
`;

const TextAndButtonsWrapper = styled.div`
  position: relative;
  z-index: ${Styles.zIndex.below};
  margin: 2.5rem 0 2.5rem 4vw;
`;

const rulesTextSerializers = {
  marks: {
    link: ({ children = [], mark = {} }: SanityBlockContentLinkProps) => {
      const [linkText] = children;
      const { href } = mark;
      return href && linkText ? (
        <StyledLink to={href}>
          <LinkText>{linkText}</LinkText> <LinkTriangle>&#9654;</LinkTriangle>
        </StyledLink>
      ) : null;
    },
  },
};

export const HomepageBannerView: React.FC<IHomepageBannerViewProps> = ({
  cupImage,
  enterButtonLabel,
  enterButtonUrl,
  header,
  rulesText,
}) => {
  const navigate = useNavigate();
  const onClick = useCallback(() => {
    navigate(enterButtonUrl);
  }, [enterButtonUrl, navigate]);

  return (
    <BannerWrapper data-testid="HomepageBannerView">
      <GameplayBackgroundGradient>
        <ConfettiContainer>
          <HomepageBannerConfetti />
        </ConfettiContainer>
        <ContentWrapper onClick={onClick}>
          {cupImage && <Cup image={cupImage} alt="" />}
          <TextAndButtonsWrapper>
            <HomepageBannerTypography centered={false} inverted content={header} />
            <nav>
              <Box margin="0 0.5rem 0 0">
                <ActionButton
                  size={ActionButtonSizes.SMALL}
                  to={enterButtonUrl}
                  variant={ActionButtonVariants.INVERSE}
                  eventName={CustomEventNames.RUTR_BUTTON_CLICK_HOME_PAGE_TIME_TO_PLAY}
                >
                  {enterButtonLabel}
                </ActionButton>
              </Box>
            </nav>
          </TextAndButtonsWrapper>
        </ContentWrapper>
        {rulesText && (
          <SanityBlockRenderer
            blockContentOverrides={{
              normal: ({ children }) => <RulesText>{children}</RulesText>,
            }}
            content={rulesText}
            serializers={rulesTextSerializers}
          />
        )}
      </GameplayBackgroundGradient>
    </BannerWrapper>
  );
};

const HomepageBanner: React.FC<IHomepageBannerProps> = ({ isAuthenticated }) => {
  const { rollUpTheRimLandingPageUrl, rollUpTheRimRulesUrl } = useLocalizedUrls();
  const { data: sanityData, loading: rutrLoading } = useGetAllRollUpTheRimsQuery();
  const enableRutr = useFlag(LaunchDarklyFlag.ENABLE_RUTR);
  const { formatMessage } = useIntl();

  const enterButtonUrl = () => {
    // If the RUTR campaign is still running
    // We have a separate LD flag to enable this component
    if (enableRutr) {
      return isAuthenticated
        ? rollUpTheRimLandingPageUrl
        : formatMessage(messages['routes.signIn']);
    }
    // If not redirect to the RUTR rules
    return rollUpTheRimRulesUrl;
  };

  if (rutrLoading || !sanityData) {
    return null;
  }

  const rollUpTheRim = sanityData?.allRollUpTheRims?.[0];
  const enterButtonLabel = rollUpTheRim?.enterButtonText?.localeRaw?.[0]?.children?.[0]?.text || '';
  const header = rollUpTheRim?.homepageHeader?.localeRaw || [];
  const cupImage = rollUpTheRim?.homepageCupImage?.locale?.app || null;
  const rulesText = rollUpTheRim?.rulesText?.localeRaw || [];

  return (
    <HomepageBannerView
      cupImage={cupImage}
      enterButtonLabel={enterButtonLabel}
      enterButtonUrl={enterButtonUrl()}
      header={header}
      rulesText={rulesText}
    />
  );
};

export default HomepageBanner;
const messages = defineMessages({
  'routes.signIn': {
    id: 'routes.signIn',
    description: 'No description',
  },
});
